// ?freeze=<id> on the task and evidence pages (plan §9 AsOfBar, spec J5 step 4). The page reads
// the frozen view while it is set; clearing it returns to the live record.
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCallback } from 'react';

// A freeze id is a positive whole number: matches "3", "42"; rejects "0", "03", "3a", "-1".
// https://regex101.com/?flavor=javascript&regex=%5E%5B1-9%5D%5Cd%7B0%2C15%7D%24&testString=3%0A42%0A0%0A03
const FREEZE_ID = /^[1-9]\d{0,15}$/;

/** The freeze id from a search value (the router may give a number or a string), else null. */
export function parseFreezeParam(value: unknown): number | null {
  const text = typeof value === 'number' || typeof value === 'string' ? String(value) : '';
  return FREEZE_ID.test(text) ? Number(text) : null;
}

export interface FreezeParam {
  readonly freezeId: number | null;
  /** Sets or clears ?freeze= and keeps the other search params (filters stay in the URL). */
  readonly setFreeze: (id: number | null) => void;
}

export function useFreezeParam(): FreezeParam {
  const search: Readonly<Record<string, unknown>> = useSearch({ strict: false });
  const navigate = useNavigate();
  const freezeId = parseFreezeParam(search['freeze']);
  const setFreeze = useCallback(
    (id: number | null) => {
      void navigate({
        to: '.',
        search: (prev: Readonly<Record<string, unknown>>) => {
          const rest = Object.fromEntries(Object.entries(prev).filter(([k]) => k !== 'freeze'));
          return id === null ? rest : { ...rest, freeze: id };
        },
      } as never);
    },
    [navigate],
  );
  return { freezeId, setFreeze };
}
